import { reportLetterheadLines } from "@/lib/reportPrintShell";
import { currentCurrency, money2 } from "@/lib/format";

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes) {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function concatBytes(parts) {
  const total = parts.reduce((sum, p) => sum + p.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  parts.forEach((p) => {
    out.set(p, offset);
    offset += p.length;
  });
  return out;
}

function u16(n) {
  return new Uint8Array([n & 0xff, (n >>> 8) & 0xff]);
}

function u32(n) {
  return new Uint8Array([n & 0xff, (n >>> 8) & 0xff, (n >>> 16) & 0xff, (n >>> 24) & 0xff]);
}

function zipStore(files) {
  const encoder = new TextEncoder();
  const localParts = [];
  const centralParts = [];
  let offset = 0;

  for (const file of files) {
    const nameBytes = encoder.encode(file.name);
    const data = typeof file.data === "string" ? encoder.encode(file.data) : file.data;
    const payload = data;
    const method = 0;
    const crc = crc32(data);
    const local = concatBytes([
      new Uint8Array([0x50, 0x4b, 0x03, 0x04]),
      u16(20),
      u16(0),
      u16(method),
      u16(0),
      u16(0),
      u32(crc),
      u32(payload.length),
      u32(data.length),
      u16(nameBytes.length),
      u16(0),
      nameBytes,
      payload,
    ]);
    const central = concatBytes([
      new Uint8Array([0x50, 0x4b, 0x01, 0x02]),
      u16(20),
      u16(20),
      u16(0),
      u16(method),
      u16(0),
      u16(0),
      u32(crc),
      u32(payload.length),
      u32(data.length),
      u16(nameBytes.length),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(0),
      u32(offset),
      nameBytes,
    ]);
    localParts.push(local);
    centralParts.push(central);
    offset += local.length;
  }

  const centralDir = concatBytes(centralParts);
  const eocd = concatBytes([
    new Uint8Array([0x50, 0x4b, 0x05, 0x06]),
    u16(0),
    u16(0),
    u16(files.length),
    u16(files.length),
    u32(centralDir.length),
    u32(offset),
    u16(0),
  ]);

  return concatBytes([...localParts, centralDir, eocd]);
}

function xmlEscape(value) {
  return String(value ?? "")
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\uFFFE\uFFFF]/g, "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function maxColCount(headers, rows) {
  let n = headers?.length || 1;
  for (const row of rows || []) {
    if (row.length > n) n = row.length;
  }
  return Math.max(n, 1);
}

function colLetter(index) {
  let n = index + 1;
  let out = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

function sanitizeSheetName(name, used) {
  let base = String(name || "Sheet")
    .replace(/[\\/?*[\]]/g, " ")
    .replace(/'/g, "")
    .trim()
    .slice(0, 31) || "Sheet";
  let candidate = base;
  let i = 2;
  while (used.has(candidate.toLowerCase())) {
    const suffix = ` (${i})`;
    candidate = `${base.slice(0, 31 - suffix.length)}${suffix}`;
    i += 1;
  }
  used.add(candidate.toLowerCase());
  return candidate;
}

function slugify(value) {
  return (
    String(value || "report")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "report"
  );
}

function cellText(el) {
  return (el?.innerText || el?.textContent || "").replace(/\s+/g, " ").trim();
}

function rowCells(tr) {
  const cells = [];
  tr.querySelectorAll(":scope > th, :scope > td").forEach((cell) => {
    const span = Math.max(1, Number(cell.getAttribute("colspan") || 1));
    const text = cellText(cell);
    cells.push(text);
    for (let i = 1; i < span; i += 1) cells.push("");
  });
  return cells;
}

function tableToSheet(table, name) {
  const headerRow =
    table.querySelector("thead tr") ||
    table.querySelector("tr");
  const headers = headerRow ? rowCells(headerRow) : [];
  const rows = [];
  table.querySelectorAll("tbody tr").forEach((tr) => {
    if (headerRow && tr === headerRow) return;
    rows.push(rowCells(tr));
  });
  table.querySelectorAll("tfoot tr").forEach((tr) => {
    rows.push(rowCells(tr));
  });
  if (!table.querySelector("thead") && !table.querySelector("tbody")) {
    const all = [...table.querySelectorAll("tr")];
    all.forEach((tr, idx) => {
      if (idx === 0) return;
      rows.push(rowCells(tr));
    });
  }
  return { name, headers, rows };
}

export function collectReportSheets(root) {
  if (!root) return [];
  const sheets = [];

  const kpi = root.querySelector('[data-testid="report-kpi-strip"]');
  if (kpi) {
    const rows = [...kpi.children]
      .map((card) => {
        const ps = card.querySelectorAll("p");
        return [cellText(ps[0]), cellText(ps[1])];
      })
      .filter((row) => row[0] || row[1]);
    if (rows.length) {
      sheets.push({ name: "Summary", headers: ["Metric", "Value"], rows });
    }
  }

  const cards = [...root.querySelectorAll('[data-testid="report-table-card"]')];
  const usedTables = new Set();
  cards.forEach((card, index) => {
    const table = card.querySelector("table");
    if (!table) return;
    usedTables.add(table);
    const title = cellText(card.querySelector("h2")) || `Table ${index + 1}`;
    const sheet = tableToSheet(table, title);
    if (sheet.headers.length || sheet.rows.length) sheets.push(sheet);
  });

  if (!cards.length) {
    root.querySelectorAll("table").forEach((table, index) => {
      if (usedTables.has(table)) return;
      if (table.closest("[role='listbox'], [data-radix-select-content]")) return;
      const sheet = tableToSheet(table, `Table ${index + 1}`);
      if (sheet.headers.length || sheet.rows.length) sheets.push(sheet);
    });
  }

  return sheets;
}

function parseAmount(value) {
  const { code } = currentCurrency();
  const codeRe = new RegExp(code.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
  const s = String(value ?? "")
    .replace(/AED|SAR|OMR|QAR|KWD|BHD/gi, "")
    .replace(codeRe, "")
    .replace(/,/g, "")
    .replace(/%/g, "")
    .trim();
  if (!s || !looksNumeric(value)) return null;
  const neg = /^\(.*\)$/.test(s);
  const n = Number(s.replace(/[()]/g, ""));
  if (Number.isNaN(n)) return null;
  return neg ? -n : n;
}

function formatAmount(n) {
  return money2(n);
}

function columnLooksNumeric(rows, colIdx) {
  let seen = 0;
  let numeric = 0;
  for (const row of rows || []) {
    const v = row[colIdx];
    if (v == null || String(v).trim() === "") continue;
    seen += 1;
    if (parseAmount(v) != null) numeric += 1;
  }
  return seen > 0 && numeric / seen >= 0.6;
}

function shouldSumColumn(header, rows, colIdx) {
  const h = String(header || "").toLowerCase();
  if (/(qty|quantity|count|bills|amount|total|sales|vat|gross|net|discount|tax|price|paid|round)/.test(h)) {
    return columnLooksNumeric(rows, colIdx);
  }
  let money = 0;
  let seen = 0;
  for (const row of rows || []) {
    const v = String(row[colIdx] ?? "").trim();
    if (!v) continue;
    seen += 1;
    if (parseAmount(v) != null && /AED|SAR|OMR|QAR|KWD|BHD|,|\.\d{1,3}\s*$/i.test(v)) money += 1;
  }
  return seen > 0 && money / seen >= 0.6;
}

function isTotalRow(row) {
  const label = String(row?.[0] ?? "").toLowerCase();
  return /\b(grand\s*)?total\b/.test(label);
}

function withGrandTotal(sheet) {
  const headers = sheet.headers || [];
  const rows = [...(sheet.rows || [])];
  if (rows.length < 2 || !headers.length) return { ...sheet, rows };
  if (String(sheet.name || "").toLowerCase() === "summary") return { ...sheet, rows };
  if (isTotalRow(rows[rows.length - 1])) {
    const last = [...rows[rows.length - 1]];
    last._total = true;
    rows[rows.length - 1] = last;
    return { ...sheet, rows };
  }

  const numericCols = headers.map((header, i) => shouldSumColumn(header, rows, i));
  if (!numericCols.some(Boolean)) return { ...sheet, rows };

  const total = headers.map((_, i) => {
    if (!numericCols[i]) return i === 0 ? "GRAND TOTAL" : "";
    const sum = rows.reduce((acc, row) => acc + (parseAmount(row[i]) || 0), 0);
    return formatAmount(sum);
  });
  if (!numericCols[0] && !String(total[0]).trim()) total[0] = "GRAND TOTAL";
  total._total = true;
  rows.push(total);
  return { ...sheet, rows };
}

function letterheadKind(line, index, title) {
  if (!line) return "blank";
  if (index === 0) return "shop";
  if (line === title) return "title";
  if (String(line).startsWith("Generated ")) return "meta";
  if (
    String(line).startsWith("Period:") ||
    String(line).startsWith("Date:") ||
    String(line).startsWith("As of ") ||
    String(line).startsWith("Open shift") ||
    String(line).includes("Payment:")
  ) {
    return "meta";
  }
  return "info";
}

function sheetXml(sheet) {
  const sections =
    sheet.sections ||
    [{ name: "", headers: sheet.headers || [], rows: sheet.rows || [] }];
  const preamble = sheet.preamble || [];
  const title = sheet.reportTitle || "";
  const colCount = Math.max(
    2,
    ...sections.map((s) => maxColCount(s.headers, s.rows)),
  );
  const lastCol = colLetter(colCount - 1);
  const xmlRows = [];
  const merges = [];

  const writeCell = (rIdx, cIdx, value, style) => {
    const ref = `${colLetter(cIdx)}${rIdx}`;
    const text = xmlEscape(value);
    if (!text) return `<c r="${ref}" s="${style}"/>`;
    return `<c r="${ref}" s="${style}" t="inlineStr"><is><t xml:space="preserve">${text}</t></is></c>`;
  };

  const writeMergedLine = (rIdx, line, kind) => {
    const style = { shop: 2, title: 4, meta: 5, info: 3, blank: 0, section: 9 }[kind] ?? 3;
    const ht = kind === "shop" ? 22 : kind === "title" || kind === "section" ? 20 : 16;
    xmlRows.push(
      `<row r="${rIdx}" ht="${ht}" customHeight="1">${writeCell(rIdx, 0, line, style)}</row>`,
    );
    merges.push(`A${rIdx}:${lastCol}${rIdx}`);
  };

  let rowNum = 1;

  const writeTable = (headers, rows) => {
    if (headers.length) {
      const cells = Array.from({ length: colCount }, (_, c) =>
        writeCell(rowNum, c, headers[c] || "", 1),
      );
      xmlRows.push(`<row r="${rowNum}">${cells.join("")}</row>`);
      rowNum += 1;
    }
    rows.forEach((row) => {
      const total = Boolean(row?._total || isTotalRow(row));
      const cells = Array.from({ length: colCount }, (_, c) => {
        const value = row[c] ?? "";
        const numeric = parseAmount(value) != null;
        const style = total ? (numeric ? 7 : 6) : numeric ? 8 : 0;
        return writeCell(rowNum, c, value, style);
      });
      xmlRows.push(`<row r="${rowNum}">${cells.join("")}</row>`);
      rowNum += 1;
    });
  };

  preamble.forEach((line, i) => {
    writeMergedLine(rowNum, line, letterheadKind(line, i, title));
    rowNum += 1;
  });

  sections.forEach((section, index) => {
    if (index > 0) {
      writeMergedLine(rowNum, "", "blank");
      rowNum += 1;
    }
    if (section.name && sections.length > 1) {
      writeMergedLine(rowNum, section.name, "section");
      rowNum += 1;
    }
    writeTable(section.headers || [], section.rows || []);
  });

  const cols = Array.from({ length: colCount }, (_, i) => {
    let maxLen = 10;
    sections.forEach((section) => {
      maxLen = Math.max(maxLen, String(section.headers?.[i] || "").length);
      for (const row of section.rows || []) {
        maxLen = Math.max(maxLen, String(row[i] || "").length);
      }
    });
    const width = Math.min(36, Math.max(12, maxLen + 2));
    return `<col min="${i + 1}" max="${i + 1}" width="${width}" customWidth="1"/>`;
  }).join("");

  const mergeXml = merges.length
    ? `<mergeCells count="${merges.length}">${merges
        .map((ref) => `<mergeCell ref="${ref}"/>`)
        .join("")}</mergeCells>`
    : "";

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <sheetViews><sheetView workbookViewId="0" showGridLines="0"/></sheetViews>
  <cols>${cols}</cols>
  <sheetData>${xmlRows.join("")}</sheetData>
  ${mergeXml}
</worksheet>`;
}

function buildXlsx(sheets, { title, period, company }) {
  const usedNames = new Set();
  const letterhead = [
    ...reportLetterheadLines(company),
    "",
    title || "Report",
    period || "",
    `Generated ${new Date().toLocaleString()}`,
    "",
  ].filter((line, i, arr) => line !== "" || arr[i - 1] !== "");
  const allSheets = [
    {
      name: title || "Report",
      reportTitle: title || "Report",
      preamble: letterhead,
      sections: sheets.map((sheet) => withGrandTotal(sheet)),
    },
  ];

  const sheetFiles = allSheets.map((sheet, i) => ({
    name: `xl/worksheets/sheet${i + 1}.xml`,
    data: sheetXml(sheet),
    displayName: sanitizeSheetName(sheet.name, usedNames),
  }));

  const workbookSheets = sheetFiles
    .map(
      (s, i) =>
        `<sheet name="${xmlEscape(s.displayName)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`,
    )
    .join("");

  const workbookRels = sheetFiles
    .map(
      (_, i) =>
        `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`,
    )
    .join("");

  const files = [
    {
      name: "[Content_Types].xml",
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  ${sheetFiles
    .map(
      (_, i) =>
        `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
    )
    .join("")}
</Types>`,
    },
    {
      name: "_rels/.rels",
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`,
    },
    {
      name: "xl/workbook.xml",
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>${workbookSheets}</sheets>
</workbook>`,
    },
    {
      name: "xl/_rels/workbook.xml.rels",
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  ${workbookRels}
  <Relationship Id="rId${sheetFiles.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`,
    },
    {
      name: "xl/styles.xml",
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="6">
    <font><sz val="11"/><name val="Calibri"/><color rgb="FF1F140F"/></font>
    <font><b/><sz val="10"/><name val="Calibri"/><color rgb="FF4A1810"/></font>
    <font><b/><sz val="16"/><name val="Calibri"/><color rgb="FF2B0B04"/></font>
    <font><sz val="10"/><name val="Calibri"/><color rgb="FF5C463C"/></font>
    <font><b/><sz val="13"/><name val="Calibri"/><color rgb="FF2B0B04"/></font>
    <font><sz val="9"/><name val="Calibri"/><color rgb="FF8B6B5E"/></font>
  </fonts>
  <fills count="3">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFFFF3E8"/><bgColor indexed="64"/></patternFill></fill>
  </fills>
  <borders count="4">
    <border><left/><right/><top/><bottom/><diagonal/></border>
    <border>
      <left/><right/>
      <top style="thin"><color rgb="FFE5D5C8"/></top>
      <bottom style="medium"><color rgb="FFF36F21"/></bottom>
      <diagonal/>
    </border>
    <border>
      <left/><right/>
      <top style="medium"><color rgb="FF2B0B04"/></top>
      <bottom style="medium"><color rgb="FF2B0B04"/></bottom>
      <diagonal/>
    </border>
    <border>
      <left/><right/>
      <top style="hair"><color rgb="FFF0E0D4"/></top>
      <bottom/><diagonal/>
    </border>
  </borders>
  <cellStyleXfs count="1"><xf/></cellStyleXfs>
  <cellXfs count="10">
    <xf xfId="0" borderId="3" applyBorder="1"/>
    <xf xfId="0" fontId="1" fillId="2" borderId="1" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf xfId="0" fontId="2" applyFont="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf xfId="0" fontId="3" applyFont="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf xfId="0" fontId="4" applyFont="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf xfId="0" fontId="5" applyFont="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf xfId="0" fontId="1" fillId="2" borderId="2" applyFont="1" applyFill="1" applyBorder="1"/>
    <xf xfId="0" fontId="1" fillId="2" borderId="2" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="right"/></xf>
    <xf xfId="0" borderId="3" applyBorder="1" applyAlignment="1"><alignment horizontal="right"/></xf>
    <xf xfId="0" fontId="4" applyFont="1" applyAlignment="1"><alignment horizontal="left" vertical="center"/></xf>
  </cellXfs>
</styleSheet>`,
    },
    ...sheetFiles.map((s) => ({ name: s.name, data: s.data })),
  ];

  return new Blob([zipStore(files)], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

function pdfEscape(text) {
  return String(text ?? "")
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)")
    .replace(/[^\x09\x20-\x7E\xA0-\xFF]/g, "?");
}

function pdfStringWidth(text, fontSize) {
  return String(text || "").length * fontSize * 0.5;
}

function truncateToWidth(text, fontSize, maxWidth) {
  const value = String(text ?? "");
  if (pdfStringWidth(value, fontSize) <= maxWidth) return value;
  let out = value;
  while (out.length && pdfStringWidth(`${out}…`, fontSize) > maxWidth) {
    out = out.slice(0, -1);
  }
  return `${out}…`;
}

function looksNumeric(value) {
  const s = String(value ?? "").trim();
  return /^(AED|SAR|OMR|QAR|KWD|BHD\s*)?[()\d,.\-]+%?$/i.test(s);
}

function buildPdf(sheets, { title, period, company }) {
  const sections = sheets.map((sheet) => withGrandTotal(sheet));
  const maxCols = Math.max(2, ...sections.map((s) => maxColCount(s.headers, s.rows)));
  const landscape = maxCols > 7;
  const pageW = landscape ? 842 : 595;
  const pageH = landscape ? 595 : 842;
  const margin = 36;
  const fontSize = maxCols > 10 ? 7 : 8;
  const rowH = fontSize + 6;
  const contentW = pageW - margin * 2;

  const objects = [];
  const pages = [];
  let current = [];
  let y = 0;

  const text = (font, size, color, x, ty, value) => {
    current.push(`${color} rg`);
    current.push("BT");
    current.push(`/F1 ${size} Tf`);
    current.push(`1 0 0 1 ${x} ${ty} Tm (${pdfEscape(value)}) Tj`);
    current.push("ET");
  };

  const wrapLines = (value, size) => {
    const maxW = contentW - 8;
    const words = String(value || "").split(/\s+/).filter(Boolean);
    const lines = [];
    let cur = "";
    words.forEach((word) => {
      const next = cur ? `${cur} ${word}` : word;
      if (pdfStringWidth(next, size) > maxW && cur) {
        lines.push(cur);
        cur = word;
      } else {
        cur = next;
      }
    });
    if (cur) lines.push(cur);
    return lines.length ? lines : [""];
  };

  const centerLine = (size, color, value) => {
    const w = pdfStringWidth(value, size);
    const x = Math.max(margin, (pageW - w) / 2);
    text(null, size, color, x, y - size, value);
    y -= size + 4;
  };

  const startPage = () => {
    current = [];
    y = pageH - margin;
    const lines = reportLetterheadLines(company);
    lines.forEach((line, i) => {
      const isName = i === 0;
      const isBranch = company?.branch_name && line.startsWith(company.branch_name);
      const size = isName ? 14 : 9;
      const color = isName
        ? "0.169 0.043 0.016"
        : isBranch
          ? "0.953 0.435 0.129"
          : "0.36 0.27 0.24";
      wrapLines(line, size).forEach((part) => centerLine(size, color, part));
    });
    y -= 4;
    wrapLines(title || "Report", 12).forEach((part) =>
      centerLine(12, "0.169 0.043 0.016", part),
    );
    if (period) {
      centerLine(8, "0.36 0.27 0.24", period);
    }
    centerLine(8, "0.36 0.27 0.24", `Generated ${new Date().toLocaleString()}`);
    y -= 8;
  };

  const flushPage = () => {
    text(
      null,
      8,
      "0.55 0.42 0.37",
      margin,
      18,
      `${company?.name || "Restaurant ERP"} · Page ${pages.length + 1}`,
    );
    pages.push([...current]);
  };

  const ensureSpace = (need) => {
    if (y - need < margin + 16) {
      flushPage();
      startPage();
    }
  };

  const drawTable = (sheet) => {
    const colCount = maxColCount(sheet.headers, sheet.rows);
    const colW = contentW / colCount;
    const drawRow = (values, header) => {
      ensureSpace(rowH + 2);
      const rowTop = y;
      if (header) {
        current.push("0.996 0.953 0.910 rg");
        current.push(`${margin} ${rowTop - rowH} ${contentW} ${rowH} re f`);
      }
      current.push("0.898 0.835 0.784 RG 0.5 w");
      current.push(`${margin} ${rowTop - rowH} ${contentW} ${rowH} re S`);
      for (let c = 1; c < colCount; c += 1) {
        const x = margin + c * colW;
        current.push(`${x} ${rowTop} m ${x} ${rowTop - rowH} l S`);
      }
      const color = header ? "0.290 0.094 0.063" : "0.12 0.08 0.06";
      values.forEach((value, c) => {
        const label = truncateToWidth(value, fontSize, colW - 8);
        const numeric = looksNumeric(value);
        const x = numeric
          ? margin + (c + 1) * colW - 4 - pdfStringWidth(label, fontSize)
          : margin + c * colW + 4;
        text(null, fontSize, color, Math.max(margin + c * colW + 2, x), rowTop - rowH + 3, label);
      });
      y -= rowH;
    };

    ensureSpace(28);
    text(null, 10, "0.169 0.043 0.016", margin, y - 10, sheet.name);
    y -= 16;
    if (sheet.headers.length) drawRow(sheet.headers, true);
    sheet.rows.forEach((row) => {
      const padded = [...row];
      while (padded.length < colCount) padded.push("");
      drawRow(padded, false);
    });
    y -= 10;
  };

  startPage();
  sections.forEach(drawTable);
  flushPage();

  const addObj = (body) => {
    objects.push(body);
    return objects.length;
  };

  const fontId = addObj("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>");
  const pageIds = pages.map((ops) => {
    const stream = ops.join("\n");
    const streamId = addObj(
      `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`,
    );
    return { streamId };
  });

  const pageObjIds = [];
  pageIds.forEach((p) => {
    pageObjIds.push(null);
  });

  const pagesId = objects.length + pageIds.length + 1;
  pageIds.forEach((p, i) => {
    const id = addObj(
      `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 ${pageW} ${pageH}] /Contents ${p.streamId} 0 R /Resources << /Font << /F1 ${fontId} 0 R >> >> >>`,
    );
    pageObjIds[i] = id;
  });
  addObj(
    `<< /Type /Pages /Kids [${pageObjIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pageObjIds.length} >>`,
  );
  const catalogId = addObj(`<< /Type /Catalog /Pages ${pagesId} 0 R >>`);

  let offset = 0;
  const chunks = ["%PDF-1.4\n"];
  const xref = [0];
  const encoder = new TextEncoder();
  offset = encoder.encode(chunks[0]).length;

  objects.forEach((body, i) => {
    const obj = `${i + 1} 0 obj\n${body}\nendobj\n`;
    chunks.push(obj);
    xref.push(offset);
    offset += encoder.encode(obj).length;
  });

  const xrefStart = offset;
  let xrefTable = `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (let i = 1; i <= objects.length; i += 1) {
    xrefTable += `${String(xref[i]).padStart(10, "0")} 00000 n \n`;
  }
  const trailer = `trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefStart}\n%%EOF`;
  chunks.push(xrefTable, trailer);

  const pdf = concatBytes(chunks.map((c) => encoder.encode(c)));
  return new Blob([pdf], { type: "application/pdf" });
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

function findReportRoot() {
  const roots = [...document.querySelectorAll("[data-report-root]")];
  const matches = roots.filter((el) =>
    el.querySelector('[data-testid="report-table-card"], [data-testid="report-kpi-strip"], table'),
  );
  return matches[matches.length - 1] || roots[roots.length - 1] || null;
}

function inputLabel(input) {
  if (input.id) {
    const byFor = document.querySelector(`label[for="${input.id.replace(/"/g, "")}"]`);
    if (byFor) return cellText(byFor);
  }
  const wrap = input.closest("div");
  return cellText(wrap?.querySelector("label")) || input.getAttribute("aria-label") || "";
}

export function collectReportPeriod(root) {
  if (!root) return "";
  const parts = [];
  const inputs = [...root.querySelectorAll('input[type="date"]')]
    .filter((el) => el.value && !el.disabled && el.offsetParent !== null)
    .map((el) => ({ label: inputLabel(el).toLowerCase(), value: el.value }));
  if (inputs.length === 1) {
    const label = inputs[0].label;
    if (label.includes("as of") || label.includes("as at")) parts.push(`As of ${inputs[0].value}`);
    else parts.push(`Date: ${inputs[0].value}`);
  } else if (inputs.length >= 2) {
    const from = inputs.find((i) => i.label.includes("from")) || inputs[0];
    const to = inputs.find((i) => i.label === "to" || i.label.startsWith("to ")) || inputs[1];
    parts.push(`Period: ${from.value} to ${to.value}`);
  }

  [...root.querySelectorAll("[data-report-filter]")].forEach((el) => {
    if (el.offsetParent === null) return;
    const label = el.getAttribute("data-report-filter-label") || "Filter";
    const value = cellText(el);
    if (!value) return;
    const lower = value.toLowerCase();
    if (lower === "all" || lower.startsWith("all ")) return;
    parts.push(`${label}: ${value}`);
  });

  return parts.join(" · ");
}

function letterheadFromUser(user) {
  const branch = user?.default_branch;
  const companyName =
    user?.branches?.find((b) => Number(b.id) === Number(branch?.id))?.company_name ||
    user?.branches?.[0]?.company_name ||
    "Restaurant";
  return {
    name: companyName,
    branch_name: branch?.name || "",
  };
}

export function exportVisibleReport({ format, title, filename, period, company, user }) {
  const root = findReportRoot();
  if (!root) {
    throw new Error("Report content not found");
  }
  const sheets = collectReportSheets(root);
  if (!sheets.length) {
    throw new Error("No report data to export");
  }
  const letterhead = company || letterheadFromUser(user);
  const periodLine = period || collectReportPeriod(root);
  const base = slugify(filename || title);
  const stamp = new Date().toISOString().slice(0, 10);
  if (format === "excel") {
    downloadBlob(buildXlsx(sheets, { title, period: periodLine, company: letterhead }), `${base}-${stamp}.xlsx`);
    return;
  }
  downloadBlob(buildPdf(sheets, { title, period: periodLine, company: letterhead }), `${base}-${stamp}.pdf`);
}

export { slugify };
